import React, { useEffect, useMemo, useState } from "react";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { canEdit } from "../lib/permissions";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast, Toaster } from "sonner";
import { fmtMoney, fmtNum } from "../lib/exports";
import { Save, RotateCcw } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

export default function CapturaVentas() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const puedeEditar = canEdit(user, "captura_ventas");
  const [fecha, setFecha] = useState(today());
  const [sucursales, setSucursales] = useState([]);
  const [sucursalId, setSucursalId] = useState("");
  const [productos, setProductos] = useState([]);
  const [cantidades, setCantidades] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/sucursales"), api.get("/productos")]).then(([rs, rp]) => {
      setSucursales(rs.data);
      setProductos(rp.data);
      if (rs.data.length) setSucursalId(user?.sucursal_default || rs.data[0].id);
    });
  }, [user]);

  const setCantidad = (pid, val) => {
    setCantidades((prev) => ({ ...prev, [pid]: val }));
  };

  const totales = useMemo(() => {
    let total = 0, unidades = 0;
    productos.forEach((p) => {
      const c = parseFloat(cantidades[p.id] || 0);
      if (c > 0) {
        total += c * (p.precio_venta || 0);
        unidades += c;
      }
    });
    return { total, unidades };
  }, [cantidades, productos]);

  const limpiar = () => setCantidades({});

  const guardar = async () => {
    if (!sucursalId) return toast.error("Selecciona una sucursal");
    const detalle = productos
      .map((p) => {
        const c = parseFloat(cantidades[p.id] || 0);
        if (!c || c <= 0) return null;
        return {
          producto_id: p.id,
          nombre: p.nombre,
          cantidad: c,
          precio: p.precio_venta || 0,
          costo: p.costo_unitario || 0,
          pollos_equivalentes: p.pollos_equivalentes || 0,
        };
      })
      .filter(Boolean);
    if (!detalle.length) return toast.error("Captura al menos una cantidad");
    setSaving(true);
    try {
      await api.post("/ventas", { fecha, sucursal_id: sucursalId, detalle });
      toast.success("Venta guardada con éxito");
      limpiar();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <Toaster richColors position="top-right" />
      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.15em] text-blue-600">Operación</div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight mt-1">Captura de Ventas</h1>
        <p className="text-sm text-slate-500 mt-1">Ingresa las cantidades vendidas por producto.</p>
      </div>

      <Card className="p-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Fecha</Label>
          <Input
            type="date"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            data-testid="venta-fecha"
            className="mt-1.5 bg-blue-50 border-blue-200 text-blue-900"
          />
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Sucursal</Label>
          <Select value={sucursalId} onValueChange={setSucursalId}>
            <SelectTrigger data-testid="venta-sucursal" className="mt-1.5 bg-blue-50 border-blue-200 text-blue-900">
              <SelectValue placeholder="Selecciona sucursal" />
            </SelectTrigger>
            <SelectContent>
              {sucursales.map((s) => <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Capturado por</Label>
          <div className="mt-1.5 px-3 py-2 bg-slate-800 text-slate-100 rounded-md text-sm font-mono">
            {user?.email}
          </div>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-100 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-600">Producto</th>
                {isAdmin && <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-600 text-right">Precio</th>}
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-600 text-center w-32">Cantidad</th>
                {isAdmin && <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-600 text-right">Subtotal</th>}
              </tr>
            </thead>
            <tbody>
              {productos.map((p) => {
                const c = parseFloat(cantidades[p.id] || 0);
                const sub = c * (p.precio_venta || 0);
                return (
                  <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-2.5 text-sm font-medium text-slate-900">{p.nombre}</td>
                    {isAdmin && (
                      <td className="px-4 py-2.5 text-sm text-slate-600 text-right font-mono">{fmtMoney(p.precio_venta)}</td>
                    )}
                    <td className="px-2 py-1.5 text-center">
                      <Input
                        type="number"
                        min="0"
                        step="any"
                        inputMode="decimal"
                        value={cantidades[p.id] || ""}
                        onChange={(e) => setCantidad(p.id, e.target.value)}
                        data-testid={`venta-cant-${p.id}`}
                        className="bg-blue-50 border-blue-200 text-blue-900 text-center font-mono h-9 w-24 mx-auto"
                      />
                    </td>
                    {isAdmin && (
                      <td className="px-4 py-2.5 text-right font-mono text-sm">
                        {sub > 0 ? (
                          <span className="inline-block px-2 py-1 bg-slate-800 text-slate-100 rounded">{fmtMoney(sub)}</span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
            {isAdmin && (
              <tfoot className="bg-slate-900 text-white">
                <tr>
                  <td className="px-4 py-3 text-sm font-semibold">TOTAL DEL DÍA</td>
                  <td className="px-4 py-3 text-right text-xs text-slate-400 uppercase">Unidades</td>
                  <td className="px-4 py-3 text-center font-mono font-bold" data-testid="venta-unidades">{fmtNum(totales.unidades, 0)}</td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-lg" data-testid="venta-total">{fmtMoney(totales.total)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </Card>

      <div className="flex flex-col sm:flex-row gap-3 sticky bottom-0 bg-slate-50 py-3 -mx-4 px-4">
        <Button
          onClick={guardar}
          disabled={saving}
          data-testid="venta-guardar"
          className="bg-blue-600 hover:bg-blue-700 text-white h-11 flex-1 sm:flex-none sm:px-8"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Guardando…" : "Guardar Ventas"}
        </Button>
        <Button variant="outline" onClick={limpiar} data-testid="venta-limpiar" className="h-11">
          <RotateCcw className="w-4 h-4 mr-2" /> Limpiar Formulario
        </Button>
      </div>
    </div>
  );
}
